<template>
  <AppModal
    :visible="isOpen === true"
    :title="`Edit Action Step (${editableData.nodeType !== undefined && editableData.nodeType !== null ? editableData.nodeType : 'Action'})`"
    :box-class="modalBoxClass"
    :render-mode="renderMode"
    :panel-plain="renderMode === 'panel'"
    @close="closeModal"
  >
    <div class="action-step-editor-shell">
      <div class="action-step-tabbar" role="tablist" aria-label="Action editor tabs">
        <button type="button" class="action-step-tab" :class="{ 'action-step-tab-active': activeTab === 'general' }" @click="activeTab = 'general'">General</button>
        <button type="button" class="action-step-tab" :class="{ 'action-step-tab-active': activeTab === 'execution' }" @click="activeTab = 'execution'">{{ executionTabLabel }}</button>
        <button type="button" class="action-step-tab" :class="{ 'action-step-tab-active': activeTab === 'data' }" @click="activeTab = 'data'">Data</button>
      </div>

      <div v-show="activeTab === 'general'" class="action-step-tab-panel">
        <div class="form-control mb-4">
          <label class="label">
            <span class="label-text">Step Name</span>
          </label>
          <input
            type="text"
            v-model="editableData.label"
            class="input input-bordered w-full"
            placeholder="Enter action step name"
            required
          />
        </div>
        <div class="form-control mb-4">
          <label class="label">
            <span class="label-text">Action Type</span>
          </label>
          <select v-model="editableData.actionType" class="select select-bordered w-full" @change="handleActionTypeChange">
            <option value="activateTool">Activate Tool</option>
            <option value="httpRequest">HTTP Request</option>
            <option value="announce">Announce</option>
            <option value="transferCall">Transfer Call</option>
            <option value="endCall">End Call</option>
          </select>
          <div class="text-xs text-gray-500 mt-2">Choose the action this step performs when the flow reaches it.</div>
        </div>
        <div class="form-control mb-4">
          <label class="label">
            <span class="label-text">{{ actionMessageLabel }}</span>
          </label>
          <textarea
            v-model="editableData.message"
            class="textarea textarea-bordered w-full"
            rows="3"
            :placeholder="actionMessagePlaceholder"
          ></textarea>
        </div>
        <div class="form-control flex flex-row mb-4 items-start">
          <label class="label cursor-pointer py-0">
            <input
              type="checkbox"
              v-model="editableData.useExactMessage"
              class="checkbox checkbox-primary mr-4"
            />
            <span class="label-text">Use exact message</span>
          </label>
        </div>
        <div class="form-control mb-6">
          <label class="label">
            <span class="label-text">Describe the Action</span>
          </label>
          <textarea
            v-model="editableData.stepDescription"
            class="textarea textarea-bordered w-full"
            rows="3"
            placeholder="Describe what this action step does"
          ></textarea>
        </div>
      </div>

      <div v-show="activeTab === 'execution'" class="action-step-tab-panel">
        <div v-if="currentActionType === 'activateTool'">
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">Tool Source</span>
            </label>
            <select v-model="editableData.toolSource" class="select select-bordered w-full">
              <option value="storedTool">Stored Tool</option>
            </select>
          </div>

          <div class="mb-6" v-if="editableData.toolSource === 'storedTool'">
            <MultiSelect
              label="Select Tool to Execute"
              :options="normalizedAvailableTools"
              v-model="selectedToolIdArray"
              idField="toolId"
              labelField="toolName"
              placeholder="Click to select a tool"
              :maxSelections="1"
              class="mb-4"
            />
          </div>

          <div class="mb-6" v-if="editableData.toolSource === 'inlineJsTool'">
            <div class="form-control mb-4">
              <label class="label">
                <span class="label-text">Snippet Name</span>
              </label>
              <input
                v-model="editableData.inlineTool.toolName"
                type="text"
                class="input input-bordered w-full"
                placeholder="runSnippet"
              />
            </div>
            <div class="form-control mb-4">
              <label class="label">
                <span class="label-text">Description</span>
              </label>
              <textarea
                v-model="editableData.inlineTool.description"
                class="textarea textarea-bordered w-full"
                rows="2"
                placeholder="Describe what this snippet does"
              ></textarea>
            </div>
            <div class="mb-4">
              <label class="label">
                <span class="label-text">Required Parameters</span>
              </label>
              <div v-if="editableData.inlineTool.requiredParams.length === 0" class="text-sm text-gray-500 mb-2">
                No required parameters defined.
              </div>
              <div v-for="(paramName, index) in editableData.inlineTool.requiredParams" :key="`required-param-${index}`" class="header-row mb-2">
                <input
                  v-model="editableData.inlineTool.requiredParams[index]"
                  class="input input-bordered flex-1"
                  placeholder="parameterName"
                />
                <button type="button" class="btn btn-sm btn-circle btn-ghost" @click="removeInlineRequiredParam(index)">✕</button>
              </div>
              <button type="button" class="btn btn-sm mt-2" @click="addInlineRequiredParam">+ Add Required Parameter</button>
            </div>
            <div class="mb-4">
              <label class="label">
                <span class="label-text">Optional Parameters</span>
              </label>
              <div v-if="editableData.inlineTool.optionalParams.length === 0" class="text-sm text-gray-500 mb-2">
                No optional parameters defined.
              </div>
              <div v-for="(paramName, index) in editableData.inlineTool.optionalParams" :key="`optional-param-${index}`" class="header-row mb-2">
                <input
                  v-model="editableData.inlineTool.optionalParams[index]"
                  class="input input-bordered flex-1"
                  placeholder="parameterName"
                />
                <button type="button" class="btn btn-sm btn-circle btn-ghost" @click="removeInlineOptionalParam(index)">✕</button>
              </div>
              <button type="button" class="btn btn-sm mt-2" @click="addInlineOptionalParam">+ Add Optional Parameter</button>
            </div>
            <div class="form-control mb-4">
              <label class="label pb-1">
                <span class="label-text">JavaScript</span>
              </label>
              <div class="action-step-editor-toolbar">
                <button type="button" class="btn btn-sm" @click="openFullscreenCodeEditor">Open Full Screen</button>
              </div>
              <MonacoEditor v-model="editableData.inlineTool.functionConfig" language="javascript" :height="inlineEditorHeight" />
              <div class="text-xs text-gray-500 mt-2">
                Available runtime values: <code class="text-info font-mono">params</code>, <code class="text-info font-mono">variables</code>, <code class="text-info font-mono">context</code>, <code class="text-info font-mono">httpRequest</code>
              </div>
            </div>
          </div>

          <div v-if="currentToolParameters.length > 0" class="action-step-mapping-box">
            <h4 class="font-semibold mb-2 text-md">Tool Input Mappings</h4>
            <div class="text-xs text-gray-500 mb-3 p-2 bg-base-200 rounded-md">
              Use previous variables with syntax like <code class="text-info font-mono">{{ variablePlaceholder }}</code>
            </div>
            <div v-for="(param, index) in currentToolParameters" :key="index" class="form-control mb-3">
              <label class="label pb-1">
                <span class="label-text font-medium">{{ param.name }} <span v-if="param.required === true" class="text-error">*</span></span>
              </label>
              <textarea
                v-model="editableData.toolInputMappings[param.name]"
                class="textarea textarea-bordered w-full textarea-small-expandable"
                :placeholder="`Value for ${param.name} or ${variablePlaceholder}`"
                rows="1"
              ></textarea>
              <label class="label pt-1" v-if="shouldShowParamDescription(param) === true">
                <span class="label-text-alt text-gray-500">{{ param.description }}</span>
              </label>
            </div>
          </div>

          <div v-if="currentToolParameters.length === 0" class="text-sm text-gray-500">
            This tool does not require any input parameters.
          </div>
        </div>

        <div v-if="currentActionType === 'httpRequest'">
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">Method</span>
            </label>
            <select v-model="editableData.config.method" class="select select-bordered w-full">
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">URL</span>
            </label>
            <textarea
              v-model="editableData.config.url"
              class="textarea textarea-bordered w-full technical-input http-url-input"
              placeholder="https://example.com/api/path"
              dir="ltr"
              rows="2"
            ></textarea>
            <div class="http-editor-actions mt-2">
              <button type="button" class="btn btn-sm" @click="clearHttpUrl">Clear URL</button>
            </div>
            <div class="text-xs text-gray-500 mt-2">Variables supported: {{ variablePlaceholder }}</div>
          </div>
          <div class="mb-6">
            <label class="label">
              <span class="label-text">Headers</span>
            </label>
            <div v-for="(header, index) in editableData.config.headersList" :key="`header-${index}`" class="http-field-row mb-2">
              <input v-model="header.key" class="input input-bordered technical-input" placeholder="Header-Name" dir="ltr" />
              <input v-model="header.value" class="input input-bordered technical-input" :placeholder="variablePlaceholder" dir="ltr" />
              <button type="button" class="btn btn-sm" @click="clearHeader(index)">Clear</button>
              <button type="button" class="btn btn-sm" @click="removeHeader(index)">Remove</button>
            </div>
            <div class="http-editor-actions mt-2">
              <button type="button" class="btn btn-sm" @click="addHeader">+ Add Header</button>
              <button type="button" class="btn btn-sm" @click="clearHeaderValues">Clear Header Values</button>
              <button type="button" class="btn btn-sm" @click="clearHeaders">Clear Headers</button>
            </div>
          </div>
          <div class="mb-6">
            <label class="label">
              <span class="label-text">Request Body Fields</span>
            </label>
            <div v-if="editableData.config.requestFields.length === 0" class="text-sm text-gray-500 mb-2">
              No body fields defined.
            </div>
            <div v-for="(field, index) in editableData.config.requestFields" :key="`request-field-${index}`" class="http-field-row mb-2">
              <input v-model="field.key" class="input input-bordered technical-input" placeholder="fieldName" dir="ltr" />
              <input v-model="field.value" class="input input-bordered technical-input" :placeholder="variablePlaceholder" dir="ltr" />
              <button type="button" class="btn btn-sm" @click="clearRequestField(index)">Clear</button>
              <button type="button" class="btn btn-sm" @click="removeRequestField(index)">Remove</button>
            </div>
            <div class="http-editor-actions mt-2">
              <button type="button" class="btn btn-sm" @click="addRequestField">+ Add Body Field</button>
              <button type="button" class="btn btn-sm" @click="clearRequestFieldValues">Clear Body Values</button>
              <button type="button" class="btn btn-sm" @click="clearRequestFields">Clear Body Fields</button>
            </div>
            <div class="text-xs text-gray-500 mt-2">When fields are defined here, they are sent as the JSON body and can use variables like {{ variablePlaceholder }}.</div>
          </div>
          <div class="form-control mb-6">
            <label class="label">
              <span class="label-text">Raw Body (JSON)</span>
            </label>
            <MonacoEditor v-model="editableData.config.body" :height="httpBodyEditorHeight" />
            <div class="http-editor-actions mt-2">
              <button type="button" class="btn btn-sm" @click="clearHttpBody">Clear Body</button>
            </div>
          </div>
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">Output Variable</span>
            </label>
            <input
              type="text"
              v-model="editableData.config.outputVariable"
              class="input input-bordered w-full technical-input"
              placeholder="httpResult"
              dir="ltr"
            />
            <div class="http-editor-actions mt-2">
              <button type="button" class="btn btn-sm" @click="clearOutputVariable">Clear Output Variable</button>
            </div>
            <div class="text-xs text-gray-500 mt-2">The full HTTP response will be saved in this variable.</div>
          </div>
          <div class="mb-6">
            <label class="label">
              <span class="label-text">Response Fields To Save As Variables</span>
            </label>
            <div v-for="(mapping, index) in editableData.config.responseMappings" :key="`response-mapping-${index}`" class="http-field-row mb-2">
              <input v-model="mapping.path" class="input input-bordered technical-input" placeholder="data.otpRequestId" dir="ltr" />
              <input v-model="mapping.variable" class="input input-bordered technical-input" placeholder="otpRequestId" dir="ltr" />
              <button type="button" class="btn btn-sm" @click="clearResponseMapping(index)">Clear</button>
              <button type="button" class="btn btn-sm" @click="removeResponseMapping(index)">Remove</button>
            </div>
            <div class="http-editor-actions mt-2">
              <button type="button" class="btn btn-sm" @click="addResponseMapping">+ Add Response Variable</button>
              <button type="button" class="btn btn-sm" @click="clearResponseMappingValues">Clear Response Variables</button>
              <button type="button" class="btn btn-sm" @click="clearResponseMappings">Clear Response Fields</button>
            </div>
          </div>
        </div>

        <div v-if="currentActionType === 'transferCall'">
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">Transfer Type</span>
            </label>
            <select v-model="editableData.config.transferType" class="select select-bordered w-full" :disabled="isTransferTypeLocked === true" @change="handleTransferTypeChange">
              <option value="phoneNumber">Phone Number</option>
              <option value="sipTransfer">SIP Transfer</option>
            </select>
            <div v-if="isTransferTypeLocked === true" class="text-xs text-gray-500 mt-2">This phone source requires SIP transfer.</div>
          </div>
          <div v-if="editableData.config.transferType === 'phoneNumber'" class="form-control mb-4">
            <label class="label">
              <span class="label-text">Phone Number</span>
            </label>
            <input
              type="text"
              v-model="editableData.config.phoneNumber"
              class="input input-bordered w-full"
              :placeholder="`Israeli number must start with 0, or ${variablePlaceholder}`"
            />
            <div class="text-xs text-gray-500 mt-2">For Israeli numbers, enter the local number with 0 at the start.</div>
          </div>
          <div v-if="editableData.config.transferType === 'sipTransfer'" class="transfer-sip-row mb-4">
            <div class="form-control transfer-sip-phone">
              <label class="label">
                <span class="label-text">Phone Number</span>
              </label>
              <input
                type="text"
                v-model="editableData.config.phoneNumber"
                class="input input-bordered w-full"
                :placeholder="`Israeli number must start with 0, or ${variablePlaceholder}`"
              />
            </div>
            <div class="form-control transfer-sip-ip">
              <label class="label">
                <span class="label-text">IP</span>
              </label>
              <input
                type="text"
                v-model="editableData.config.sipIp"
                class="input input-bordered w-full"
                placeholder="127.0.0.1"
              />
            </div>
            <div class="form-control transfer-sip-port">
              <label class="label">
                <span class="label-text">Port</span>
              </label>
              <input
                type="number"
                min="1"
                max="65535"
                v-model="editableData.config.sipPort"
                class="input input-bordered w-full"
                placeholder="5060"
              />
            </div>
          </div>
          <div v-if="editableData.config.transferType === 'sipTransfer'" class="text-xs text-gray-500 mb-4">The SIP destination is built from the phone number, IP, and port fields.</div>
          <div v-if="transferPhoneWarning !== ''" class="text-xs text-error mb-4">{{ transferPhoneWarning }}</div>
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">Final transfer destination</span>
            </label>
            <input
              type="text"
              :value="resolvedTransferDestination"
              class="input input-bordered w-full"
              disabled
              placeholder="Destination will be created from the fields above"
            />
          </div>
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">Transfer Timing</span>
            </label>
            <select v-model="editableData.config.delayMode" class="select select-bordered w-full">
              <option value="afterAssistantReply">After assistant reply</option>
              <option value="">No delay</option>
            </select>
          </div>
          <div class="form-control mb-4">
            <label class="label">
              <span class="label-text">Delay milliseconds</span>
            </label>
            <input
              type="number"
              min="0"
              v-model="editableData.config.delayMs"
              class="input input-bordered w-full"
              placeholder="0"
            />
          </div>
        </div>

        <div v-if="currentActionType === 'endCall'">
          <div class="text-sm text-gray-500 mb-4">Set the message before hanging up in the General tab. The call will hang up after that message is spoken.</div>
        </div>

        <div v-if="currentActionType === 'announce'">
          <div class="form-control flex flex-row mb-4 items-start">
            <label class="label cursor-pointer py-0">
              <input
                type="checkbox"
                v-model="editableData.config.allowUserInterrupt"
                class="checkbox checkbox-primary mr-4"
              />
              <span class="label-text">Allow the user to stop it by talking</span>
            </label>
          </div>
          <div class="text-sm text-gray-500">When disabled, the announcement is locked until the AI finishes speaking.</div>
        </div>
      </div>

      <div v-show="activeTab === 'data'" class="action-step-tab-panel">
        <div class="mb-6">
          <label class="label">
            <span class="label-text">Data to Collect (Optional variables to extract from conversation before or after action)</span>
          </label>
          <div v-for="(variable, index) in editableData.variablesToCollect" :key="index" class="p-3 bg-base-200 rounded-lg mb-3">
            <div class="flex items-center mb-2">
              <input
                type="text"
                v-model="variable.name"
                class="input input-bordered input-sm flex-grow mr-2"
                placeholder="Variable name"
              />
              <button type="button" @click="editableData.variablesToCollect.splice(index, 1)" class="btn btn-sm btn-circle btn-ghost">✕</button>
            </div>
            <div class="action-step-variable-row mb-2">
              <select v-model="variable.type" class="select select-bordered select-sm w-full">
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
                <option value="date">Date</option>
              </select>
              <input
                type="text"
                v-model="variable.description"
                class="input input-bordered input-sm w-full"
                placeholder="Description"
              />
            </div>
            <div class="form-control">
              <label class="label cursor-pointer justify-start">
                <input type="checkbox" v-model="variable.required" class="checkbox checkbox-xs mr-4" />
                <span class="label-text text-xs">Required field</span>
              </label>
            </div>
            <div class="action-step-variable-row mb-2">
              <select v-model="variable.validationPattern" class="select select-bordered select-sm w-full">
                <option v-for="option in regexValidationOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
              <input
                v-if="variable.validationPattern === 'custom'"
                type="text"
                v-model="variable.customRegex"
                class="input input-bordered input-sm w-full"
                placeholder="Custom regex, for example ^[A-Z]{2}\\d{6}$"
              />
            </div>
          </div>
          <button type="button" @click="addVariable" class="btn btn-sm w-full mt-2">+ Add Data</button>
        </div>
      </div>
    </div>

    <template #actions>
      <button type="button" class="btn" @click="closeModal">Cancel</button>
      <button type="button" class="btn" @click="saveChanges">Save</button>
    </template>
  </AppModal>

  <Teleport to="body">
    <div v-if="isFullscreenCodeEditorOpen === true" class="action-step-fullscreen-shell">
      <div class="action-step-fullscreen-backdrop" @click="closeFullscreenCodeEditor"></div>
      <div class="action-step-fullscreen-panel">
        <div class="action-step-fullscreen-header">
          <div class="action-step-fullscreen-title-wrap">
            <div class="action-step-fullscreen-eyebrow">JavaScript Snippet</div>
            <h3 class="action-step-fullscreen-title">{{ fullscreenEditorTitle }}</h3>
          </div>
          <div class="action-step-fullscreen-actions">
            <button type="button" class="btn btn-sm" @click="closeFullscreenCodeEditor">Close</button>
          </div>
        </div>
        <div class="action-step-fullscreen-body">
          <MonacoEditor v-model="editableData.inlineTool.functionConfig" language="javascript" theme="vs-dark" :height="fullscreenEditorHeight" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import MultiSelect from './MultiSelect.vue';
import MonacoEditor from './MonacoEditor.vue';
import AppModal from './AppModal.vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  nodeData: {
    type: Object,
    default: () => ({})
  },
  availableTools: {
    type: Array,
    default: () => []
  },
  allFlowNodes: {
    type: Array,
    default: () => []
  },
  renderMode: {
    type: String,
    default: 'modal'
  }
});

const emit = defineEmits(['close', 'save']);

const editableData = ref({});
const selectedToolIdArray = ref([]);
const variablePlaceholder = '{{variableName}}';
const activeTab = ref('general');
const isFullscreenCodeEditorOpen = ref(false);
const regexValidationOptions = [
  { value: '', label: 'No regex validation' },
  { value: 'digits', label: 'Digits only' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone number' },
  { value: 'israeliId', label: 'Israeli ID' },
  { value: 'dateIso', label: 'Date YYYY-MM-DD' },
  { value: 'time24h', label: 'Time HH:mm' },
  { value: 'uuid', label: 'UUID' },
  { value: 'custom', label: 'Custom regex' }
];
const modalBoxClass = computed(() => {
  if (props.renderMode === 'panel') {
    return 'action-step-panel-shell';
  }
  return 'w-11/12 max-w-4xl action-step-modal-box';
});

function getDefaultInlineTool() {
  return {
    toolName: '',
    description: '',
    requiredParams: [],
    optionalParams: [],
    functionConfig: ''
  };
}

function getDefaultHttpConfig() {
  return {
    method: 'POST',
    url: '',
    headersList: [],
    requestFields: [],
    body: '{}',
    outputVariable: '',
    responseMappings: []
  };
}

function normalizeParamNames(rawParams) {
  const values = Array.isArray(rawParams) === true ? rawParams : [];
  const normalized = [];
  for (let i = 0; i < values.length; i = i + 1) {
    const value = typeof values[i] === 'string' ? values[i].trim() : '';
    if (value.length > 0 && normalized.includes(value) === false) {
      normalized.push(value);
    }
  }
  return normalized;
}

function normalizeInlineTool(rawTool) {
  const nextTool = rawTool !== null && rawTool !== undefined && typeof rawTool === 'object' ? JSON.parse(JSON.stringify(rawTool)) : {};
  if (typeof nextTool.toolName !== 'string') {
    nextTool.toolName = '';
  }
  if (typeof nextTool.description !== 'string') {
    nextTool.description = '';
  }
  if (typeof nextTool.functionConfig !== 'string') {
    nextTool.functionConfig = '';
  }
  nextTool.requiredParams = normalizeParamNames(nextTool.requiredParams);
  nextTool.optionalParams = normalizeParamNames(nextTool.optionalParams).filter(paramName => nextTool.requiredParams.includes(paramName) === false);
  return { ...getDefaultInlineTool(), ...nextTool };
}

function getToolDataPayload(tool) {
  if (tool !== null && tool !== undefined && typeof tool === 'object') {
    if (tool.toolData !== null && tool.toolData !== undefined && typeof tool.toolData === 'object') {
      return tool.toolData;
    }
    if (tool.tooldata !== null && tool.tooldata !== undefined && typeof tool.tooldata === 'object') {
      return tool.tooldata;
    }
    if (typeof tool.toolData === 'string') {
      try {
        const parsedToolData = JSON.parse(tool.toolData);
        if (parsedToolData !== null && parsedToolData !== undefined && typeof parsedToolData === 'object') {
          return parsedToolData;
        }
      } catch (error) {}
    }
    if (typeof tool.tooldata === 'string') {
      try {
        const parsedToolData = JSON.parse(tool.tooldata);
        if (parsedToolData !== null && parsedToolData !== undefined && typeof parsedToolData === 'object') {
          return parsedToolData;
        }
      } catch (error) {}
    }
  }
  return {};
}

function buildToolParameterDefinitions(toolData) {
  const safeToolData = toolData !== null && toolData !== undefined && typeof toolData === 'object' ? toolData : {};
  const requiredParams = normalizeParamNames(safeToolData.requiredParams);
  const optionalParams = normalizeParamNames(safeToolData.optionalParams).filter(paramName => requiredParams.includes(paramName) === false);
  const params = [];
  for (let i = 0; i < requiredParams.length; i = i + 1) {
    params.push({ name: requiredParams[i], required: true, description: '' });
  }
  for (let i = 0; i < optionalParams.length; i = i + 1) {
    params.push({ name: optionalParams[i], required: false, description: '' });
  }
  return params;
}

function normalizeHttpConfig(config) {
  const baseConfig = config !== null && config !== undefined && typeof config === 'object' ? JSON.parse(JSON.stringify(config)) : {};
  if (Array.isArray(baseConfig.headersList) !== true) {
    baseConfig.headersList = [];
  }
  if (Array.isArray(baseConfig.requestFields) !== true) {
    baseConfig.requestFields = [];
  }
  if (typeof baseConfig.body !== 'string') {
    baseConfig.body = '{}';
  }
  if (Array.isArray(baseConfig.responseMappings) !== true) {
    baseConfig.responseMappings = [];
  }
  if (typeof baseConfig.method !== 'string' || baseConfig.method.trim().length === 0) {
    baseConfig.method = 'POST';
  }
  if (typeof baseConfig.url !== 'string') {
    baseConfig.url = '';
  }
  if (typeof baseConfig.outputVariable !== 'string') {
    baseConfig.outputVariable = '';
  }
  return { ...getDefaultHttpConfig(), ...baseConfig };
}

function getTrimmedConfigValue(config, key) {
  if (config !== null && config !== undefined && typeof config === 'object' && typeof config[key] === 'string') {
    return config[key].trim();
  }
  return '';
}

function isSipTransferSource(config) {
  if (config === null || config === undefined || typeof config !== 'object') {
    return false;
  }
  if (config.lockTransferType === true) {
    return true;
  }
  if (config.phoneNumberType === 'sip') {
    return true;
  }
  if (config.provider === 'partner') {
    return true;
  }
  const destination = getTrimmedConfigValue(config, 'destination').toLowerCase();
  if (destination.startsWith('sip:') === true) {
    return true;
  }
  return false;
}

function getTransferType(config) {
  if (isSipTransferSource(config) === true) {
    return 'sipTransfer';
  }
  if (config !== null && config !== undefined && typeof config === 'object' && config.transferType === 'sipTransfer') {
    return 'sipTransfer';
  }
  return 'phoneNumber';
}

function resolvePhoneNumberFromConfig(config) {
  const phoneNumber = getTrimmedConfigValue(config, 'phoneNumber');
  if (phoneNumber.length > 0) {
    return phoneNumber;
  }
  const destination = getTrimmedConfigValue(config, 'destination');
  if (destination.length === 0) {
    return '';
  }
  const sipMatch = destination.match(/^sip:([^@]+)@/i);
  if (sipMatch !== null && typeof sipMatch[1] === 'string') {
    return sipMatch[1].trim();
  }
  if (destination.indexOf('@') > 0) {
    return destination.slice(0, destination.indexOf('@')).replace(/^sip:/i, '').trim();
  }
  return destination;
}

function resolveSipIpFromConfig(config) {
  const sipIp = getTrimmedConfigValue(config, 'sipIp');
  if (sipIp.length > 0) {
    return sipIp;
  }
  const destination = getTrimmedConfigValue(config, 'destination');
  const sipMatch = destination.match(/@([^:]+)(?::\d+)?/);
  if (sipMatch !== null && typeof sipMatch[1] === 'string') {
    return sipMatch[1].trim();
  }
  return '';
}

function resolveSipPortFromConfig(config) {
  const sipPort = getTrimmedConfigValue(config, 'sipPort');
  if (sipPort.length > 0) {
    return sipPort;
  }
  const destination = getTrimmedConfigValue(config, 'destination');
  const sipMatch = destination.match(/:(\d{2,5})$/);
  if (sipMatch !== null && typeof sipMatch[1] === 'string') {
    return sipMatch[1].trim();
  }
  return '';
}

function buildTransferDestination(config) {
  if (config === null || config === undefined || typeof config !== 'object') {
    return '';
  }
  const transferType = config.transferType === 'sipTransfer' ? 'sipTransfer' : 'phoneNumber';
  const phoneNumber = getTrimmedConfigValue(config, 'phoneNumber');
  if (transferType === 'phoneNumber') {
    return phoneNumber;
  }
  const sipIp = getTrimmedConfigValue(config, 'sipIp');
  const sipPort = getTrimmedConfigValue(config, 'sipPort');
  if (phoneNumber.length === 0 || sipIp.length === 0 || sipPort.length === 0) {
    return '';
  }
  return `sip:${phoneNumber}@${sipIp}:${sipPort}`;
}

function normalizeAnnounceConfig(config) {
  const baseConfig = config !== null && config !== undefined && typeof config === 'object' ? JSON.parse(JSON.stringify(config)) : {};
  if (baseConfig.allowUserInterrupt !== false) {
    baseConfig.allowUserInterrupt = true;
  }
  return {
    allowUserInterrupt: baseConfig.allowUserInterrupt
  };
}

function normalizeCallControlConfig(config, actionType) {
  const baseConfig = config !== null && config !== undefined && typeof config === 'object' ? JSON.parse(JSON.stringify(config)) : {};
  if (typeof baseConfig.delayMode !== 'string') {
    baseConfig.delayMode = 'afterAssistantReply';
  }
  if (baseConfig.delayMode !== 'afterAssistantReply' && baseConfig.delayMode !== '') {
    baseConfig.delayMode = 'afterAssistantReply';
  }
  const delayValue = Number(baseConfig.delayMs);
  baseConfig.delayMs = Number.isFinite(delayValue) === true && delayValue >= 0 ? Math.floor(delayValue) : 0;
  if (actionType === 'transferCall') {
    baseConfig.transferType = getTransferType(baseConfig);
    baseConfig.phoneNumber = resolvePhoneNumberFromConfig(baseConfig);
    baseConfig.sipIp = resolveSipIpFromConfig(baseConfig);
    baseConfig.sipPort = resolveSipPortFromConfig(baseConfig);
    baseConfig.destination = buildTransferDestination(baseConfig);
    baseConfig.lockTransferType = isSipTransferSource(baseConfig);
    return {
      destination: baseConfig.destination,
      phoneNumber: baseConfig.phoneNumber,
      sipIp: baseConfig.sipIp,
      sipPort: baseConfig.sipPort,
      transferType: baseConfig.transferType,
      lockTransferType: baseConfig.lockTransferType,
      delayMode: baseConfig.delayMode,
      delayMs: baseConfig.delayMs
    };
  }
  return {
    delayMode: baseConfig.delayMode,
    delayMs: baseConfig.delayMs
  };
}

function normalizeEditableData(rawData) {
  const nextData = rawData !== null && rawData !== undefined ? JSON.parse(JSON.stringify(rawData)) : {};
  if (nextData.nodeType === undefined) {
    nextData.nodeType = 'Action';
  }
  if (typeof nextData.label !== 'string') {
    nextData.label = 'Action Step';
  }
  if (typeof nextData.message !== 'string') {
    nextData.message = '';
  }
  if (typeof nextData.stepDescription !== 'string') {
    nextData.stepDescription = '';
  }
  if (nextData.useExactMessage !== true) {
    nextData.useExactMessage = false;
  }
  if (Array.isArray(nextData.variablesToCollect) !== true) {
    nextData.variablesToCollect = [];
  } else {
    nextData.variablesToCollect = nextData.variablesToCollect.map(v => {
      if (typeof v === 'string') {
        return { name: v, type: 'string', description: '', required: false, validationPattern: '', customRegex: '' };
      }
      return {
        name: typeof v?.name === 'string' ? v.name : '',
        type: typeof v?.type === 'string' ? v.type : 'string',
        description: typeof v?.description === 'string' ? v.description : '',
        required: v?.required === true,
        validationPattern: typeof v?.validationPattern === 'string' ? v.validationPattern : '',
        customRegex: typeof v?.customRegex === 'string' ? v.customRegex : ''
      };
    });
  }
  if (typeof nextData.actionType !== 'string' || nextData.actionType.trim().length === 0) {
    nextData.actionType = 'activateTool';
  }
  if (nextData.actionType === 'httpRequest') {
    nextData.config = normalizeHttpConfig(nextData.config);
  } else if (nextData.actionType === 'announce') {
    nextData.config = normalizeAnnounceConfig(nextData.config);
    nextData.selectedToolId = null;
    nextData.toolInputMappings = {};
    nextData.inlineTool = normalizeInlineTool(nextData.inlineTool);
  } else if (nextData.actionType === 'transferCall' || nextData.actionType === 'endCall') {
    nextData.config = normalizeCallControlConfig(nextData.config, nextData.actionType);
    nextData.selectedToolId = null;
    nextData.toolInputMappings = {};
    nextData.inlineTool = normalizeInlineTool(nextData.inlineTool);
  } else {
    if (typeof nextData.toolSource !== 'string' || nextData.toolSource.trim().length === 0) {
      nextData.toolSource = 'storedTool';
    }
    if (nextData.toolSource === 'inlineJsTool') {
      nextData.toolSource = 'storedTool';
    }
    if (nextData.selectedToolId === undefined) {
      nextData.selectedToolId = null;
    }
    if (nextData.toolInputMappings === undefined || nextData.toolInputMappings === null || typeof nextData.toolInputMappings !== 'object') {
      nextData.toolInputMappings = {};
    }
    nextData.inlineTool = normalizeInlineTool(nextData.inlineTool);
    if (nextData.config === undefined || nextData.config === null || typeof nextData.config !== 'object') {
      nextData.config = {};
    }
  }
  return nextData;
}

const currentActionType = computed(() => {
  return editableData.value !== null && editableData.value !== undefined && typeof editableData.value.actionType === 'string' && editableData.value.actionType.trim().length > 0
    ? editableData.value.actionType
    : 'activateTool';
});

const actionMessageLabel = computed(() => {
  if (currentActionType.value === 'endCall') {
    return 'Message before hanging up';
  }
  if (currentActionType.value === 'announce') {
    return 'Announcement text';
  }
  return 'Message to say to the user';
});

const actionMessagePlaceholder = computed(() => {
  if (currentActionType.value === 'endCall') {
    return 'Enter the final message the agent should say before hanging up';
  }
  if (currentActionType.value === 'announce') {
    return 'Enter the announcement the AI should say';
  }
  return 'Enter the message the agent should say at this step';
});

const isTransferTypeLocked = computed(() => {
  if (editableData.value === null || editableData.value === undefined || editableData.value.config === null || editableData.value.config === undefined) {
    return false;
  }
  return editableData.value.config.lockTransferType === true;
});

const resolvedTransferDestination = computed(() => {
  if (editableData.value === null || editableData.value === undefined || editableData.value.config === null || editableData.value.config === undefined) {
    return '';
  }
  return buildTransferDestination(editableData.value.config);
});

const transferPhoneWarning = computed(() => {
  if (currentActionType.value !== 'transferCall') {
    return '';
  }
  if (editableData.value === null || editableData.value === undefined || editableData.value.config === null || editableData.value.config === undefined) {
    return '';
  }
  const phoneNumber = getTrimmedConfigValue(editableData.value.config, 'phoneNumber');
  if (phoneNumber.length === 0) {
    return '';
  }
  if (/^\{\{.+\}\}$/.test(phoneNumber) === true) {
    return '';
  }
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  if (digitsOnly.length >= 9 && digitsOnly.length <= 10 && digitsOnly.startsWith('972') === false && digitsOnly.startsWith('0') === false) {
    return 'Israeli phone numbers must start with 0.';
  }
  return '';
});

const executionTabLabel = computed(() => {
  if (currentActionType.value === 'httpRequest') {
    return 'Request';
  }
  if (currentActionType.value === 'announce') {
    return 'Announce';
  }
  if (currentActionType.value === 'transferCall') {
    return 'Transfer';
  }
  if (currentActionType.value === 'endCall') {
    return 'Call End';
  }
  if (editableData.value !== null && editableData.value !== undefined && editableData.value.toolSource === 'inlineJsTool') {
    return 'JavaScript';
  }
  return 'Tool';
});

const inlineEditorHeight = computed(() => {
  if (props.renderMode === 'panel') {
    return '360px';
  }
  return '260px';
});

const fullscreenEditorTitle = computed(() => {
  if (editableData.value !== null && editableData.value !== undefined) {
    if (editableData.value.inlineTool !== undefined && editableData.value.inlineTool !== null && typeof editableData.value.inlineTool.toolName === 'string' && editableData.value.inlineTool.toolName.trim().length > 0) {
      return editableData.value.inlineTool.toolName.trim();
    }
    if (typeof editableData.value.label === 'string' && editableData.value.label.trim().length > 0) {
      return editableData.value.label.trim();
    }
  }
  return 'JS Snippet';
});

const fullscreenEditorHeight = computed(() => {
  return 'calc(100vh - 150px)';
});

const httpBodyEditorHeight = computed(() => {
  if (props.renderMode === 'panel') {
    return '260px';
  }
  return '220px';
});

const normalizedAvailableTools = computed(() => {
  const tools = Array.isArray(props.availableTools) === true ? props.availableTools : [];
  const normalized = [];
  for (let i = 0; i < tools.length; i = i + 1) {
    const tool = tools[i];
    if (tool === undefined || tool === null || typeof tool !== 'object') {
      continue;
    }
    const toolIdRaw = tool.toolId !== undefined ? tool.toolId : tool.toolid;
    const toolId = Number.parseInt(String(toolIdRaw), 10);
    if (Number.isFinite(toolId) !== true || toolId <= 0) {
      continue;
    }
    const toolNameRaw = tool.toolName !== undefined ? tool.toolName : tool.toolname;
    const toolName = typeof toolNameRaw === 'string' && toolNameRaw.trim().length > 0 ? toolNameRaw.trim() : `Tool ${toolId}`;
    normalized.push({
      toolId,
      toolName,
      toolData: getToolDataPayload(tool)
    });
  }
  return normalized;
});

const selectedTool = computed(() => {
  if (currentActionType.value !== 'activateTool') {
    return null;
  }
  if (editableData.value.toolSource !== 'storedTool') {
    return null;
  }
  if (editableData.value.selectedToolId === null) return null;
  if (editableData.value.selectedToolId === undefined) return null;
  return normalizedAvailableTools.value.find(t => t.toolId === editableData.value.selectedToolId);
});

const currentToolParameters = computed(() => {
  if (currentActionType.value !== 'activateTool') {
    return [];
  }
  if (editableData.value.toolSource === 'inlineJsTool') {
    return buildToolParameterDefinitions(editableData.value.inlineTool);
  }
  if (selectedTool.value === null || selectedTool.value === undefined) {
    return [];
  }
  return buildToolParameterDefinitions(selectedTool.value.toolData);
});

function shouldShowParamDescription(param) {
  if (param.description === undefined) return false;
  if (param.description === null) return false;
  if (param.description === '') return false;
  return true;
}

function handleActionTypeChange() {
  if (currentActionType.value === 'httpRequest') {
    isFullscreenCodeEditorOpen.value = false;
    editableData.value.config = normalizeHttpConfig(editableData.value.config);
    editableData.value.selectedToolId = null;
    editableData.value.toolInputMappings = {};
    selectedToolIdArray.value = [];
    if (typeof editableData.value.label !== 'string' || editableData.value.label.trim().length === 0 || editableData.value.label === 'Action Step') {
      editableData.value.label = 'HTTP Request';
    }
    activeTab.value = 'execution';
    return;
  }
  if (currentActionType.value === 'transferCall') {
    isFullscreenCodeEditorOpen.value = false;
    editableData.value.config = normalizeCallControlConfig(editableData.value.config, 'transferCall');
    editableData.value.selectedToolId = null;
    editableData.value.toolInputMappings = {};
    selectedToolIdArray.value = [];
    if (typeof editableData.value.label !== 'string' || editableData.value.label.trim().length === 0 || editableData.value.label === 'Action Step' || editableData.value.label === 'HTTP Request' || editableData.value.label === 'End Call') {
      editableData.value.label = 'Transfer Call';
    }
    activeTab.value = 'execution';
    return;
  }
  if (currentActionType.value === 'announce') {
    isFullscreenCodeEditorOpen.value = false;
    editableData.value.config = normalizeAnnounceConfig(editableData.value.config);
    editableData.value.selectedToolId = null;
    editableData.value.toolInputMappings = {};
    selectedToolIdArray.value = [];
    editableData.value.useExactMessage = true;
    if (typeof editableData.value.label !== 'string' || editableData.value.label.trim().length === 0 || editableData.value.label === 'Action Step' || editableData.value.label === 'HTTP Request' || editableData.value.label === 'Transfer Call' || editableData.value.label === 'End Call') {
      editableData.value.label = 'Announce';
    }
    activeTab.value = 'general';
    return;
  }
  if (currentActionType.value === 'endCall') {
    isFullscreenCodeEditorOpen.value = false;
    editableData.value.config = normalizeCallControlConfig(editableData.value.config, 'endCall');
    editableData.value.selectedToolId = null;
    editableData.value.toolInputMappings = {};
    selectedToolIdArray.value = [];
    if (typeof editableData.value.label !== 'string' || editableData.value.label.trim().length === 0 || editableData.value.label === 'Action Step' || editableData.value.label === 'HTTP Request' || editableData.value.label === 'Transfer Call') {
      editableData.value.label = 'End Call';
    }
    editableData.value.config.delayMode = 'afterAssistantReply';
    activeTab.value = 'execution';
    return;
  }
  if (typeof editableData.value.toolSource !== 'string' || editableData.value.toolSource.trim().length === 0) {
    editableData.value.toolSource = 'storedTool';
  }
  editableData.value.selectedToolId = editableData.value.selectedToolId !== undefined ? editableData.value.selectedToolId : null;
  editableData.value.toolInputMappings = editableData.value.toolInputMappings !== undefined && editableData.value.toolInputMappings !== null ? editableData.value.toolInputMappings : {};
  editableData.value.inlineTool = normalizeInlineTool(editableData.value.inlineTool);
  if (editableData.value.toolSource !== 'inlineJsTool') {
    isFullscreenCodeEditorOpen.value = false;
  }
  activeTab.value = 'execution';
}

function handleTransferTypeChange() {
  if (editableData.value === null || editableData.value === undefined || editableData.value.config === null || editableData.value.config === undefined) {
    return;
  }
  if (isTransferTypeLocked.value === true) {
    editableData.value.config.transferType = 'sipTransfer';
  }
  editableData.value.config = normalizeCallControlConfig(editableData.value.config, 'transferCall');
}

watch(() => props.nodeData, newData => {
  editableData.value = normalizeEditableData(newData);
  selectedToolIdArray.value = editableData.value.selectedToolId !== null && editableData.value.selectedToolId !== undefined ? [editableData.value.selectedToolId] : [];
  isFullscreenCodeEditorOpen.value = false;
  if (editableData.value.actionType === 'httpRequest' || editableData.value.actionType === 'transferCall' || editableData.value.actionType === 'endCall' || editableData.value.toolSource === 'inlineJsTool') {
    activeTab.value = 'execution';
  } else {
    activeTab.value = 'general';
  }
}, { immediate: true, deep: true });

watch(() => editableData.value.toolSource, newValue => {
  if (newValue !== 'inlineJsTool') {
    isFullscreenCodeEditorOpen.value = false;
  }
});

watch(selectedToolIdArray, newVal => {
  if (currentActionType.value !== 'activateTool') {
    return;
  }
  if (editableData.value !== null && editableData.value !== undefined) {
    const newSelectedId = newVal !== null && newVal !== undefined && newVal.length > 0 ? newVal[0] : null;
    if (editableData.value.selectedToolId !== newSelectedId) {
      editableData.value.selectedToolId = newSelectedId;
      editableData.value.toolInputMappings = {};
    }
  }
});

function addInlineRequiredParam() {
  editableData.value.inlineTool = normalizeInlineTool(editableData.value.inlineTool);
  editableData.value.inlineTool.requiredParams.push('');
}

function removeInlineRequiredParam(index) {
  editableData.value.inlineTool = normalizeInlineTool(editableData.value.inlineTool);
  editableData.value.inlineTool.requiredParams.splice(index, 1);
}

function addInlineOptionalParam() {
  editableData.value.inlineTool = normalizeInlineTool(editableData.value.inlineTool);
  editableData.value.inlineTool.optionalParams.push('');
}

function removeInlineOptionalParam(index) {
  editableData.value.inlineTool = normalizeInlineTool(editableData.value.inlineTool);
  editableData.value.inlineTool.optionalParams.splice(index, 1);
}

function addVariable() {
  if (editableData.value.variablesToCollect === undefined || editableData.value.variablesToCollect === null) {
    editableData.value.variablesToCollect = [];
  }
  editableData.value.variablesToCollect.push({ name: '', type: 'string', description: '', required: false, validationPattern: '', customRegex: '' });
}

function addHeader() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.headersList.push({ key: '', value: '' });
}

function clearHttpUrl() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.url = '';
}

function removeHeader(index) {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.headersList.splice(index, 1);
}

function clearHeader(index) {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  const header = editableData.value.config.headersList[index];
  if (header !== undefined && header !== null) {
    header.key = '';
    header.value = '';
  }
}

function clearHeaderValues() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  const headers = editableData.value.config.headersList;
  for (let index = 0; index < headers.length; index = index + 1) {
    headers[index].value = '';
  }
}

function clearHeaders() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.headersList = [];
}

function addRequestField() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.requestFields.push({ key: '', value: '' });
}

function removeRequestField(index) {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.requestFields.splice(index, 1);
}

function clearRequestField(index) {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  const field = editableData.value.config.requestFields[index];
  if (field !== undefined && field !== null) {
    field.key = '';
    field.value = '';
  }
}

function clearRequestFieldValues() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  const fields = editableData.value.config.requestFields;
  for (let index = 0; index < fields.length; index = index + 1) {
    fields[index].value = '';
  }
}

function clearRequestFields() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.requestFields = [];
}

function clearHttpBody() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.body = '';
}

function clearOutputVariable() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.outputVariable = '';
}

function addResponseMapping() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.responseMappings.push({ path: '', variable: '' });
}

function removeResponseMapping(index) {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.responseMappings.splice(index, 1);
}

function clearResponseMapping(index) {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  const mapping = editableData.value.config.responseMappings[index];
  if (mapping !== undefined && mapping !== null) {
    mapping.path = '';
    mapping.variable = '';
  }
}

function clearResponseMappingValues() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  const mappings = editableData.value.config.responseMappings;
  for (let index = 0; index < mappings.length; index = index + 1) {
    mappings[index].variable = '';
  }
}

function clearResponseMappings() {
  editableData.value.config = normalizeHttpConfig(editableData.value.config);
  editableData.value.config.responseMappings = [];
}

function openFullscreenCodeEditor() {
  if (currentActionType.value !== 'activateTool') {
    return;
  }
  if (editableData.value === undefined || editableData.value === null || editableData.value.toolSource !== 'inlineJsTool') {
    return;
  }
  isFullscreenCodeEditorOpen.value = true;
}

function closeFullscreenCodeEditor() {
  isFullscreenCodeEditorOpen.value = false;
}

function closeModal() {
  isFullscreenCodeEditorOpen.value = false;
  emit('close');
}

function saveChanges() {
  if (editableData.value.variablesToCollect !== undefined && editableData.value.variablesToCollect !== null) {
    editableData.value.variablesToCollect = editableData.value.variablesToCollect.filter(v => (v.name !== undefined && v.name !== null) && v.name.trim().length > 0);
    for (let index = 0; index < editableData.value.variablesToCollect.length; index = index + 1) {
      const variable = editableData.value.variablesToCollect[index];
      if (variable.validationPattern !== 'custom') {
        variable.customRegex = '';
      }
    }
  }
  if (currentActionType.value === 'httpRequest') {
    editableData.value.config = normalizeHttpConfig(editableData.value.config);
    editableData.value.config.headersList = editableData.value.config.headersList.filter(item => {
      return typeof item?.key === 'string' && item.key.trim().length > 0;
    });
    editableData.value.config.requestFields = editableData.value.config.requestFields.filter(item => {
      return typeof item?.key === 'string' && item.key.trim().length > 0;
    });
    editableData.value.config.responseMappings = editableData.value.config.responseMappings.filter(item => {
      return typeof item?.path === 'string' && item.path.trim().length > 0 && typeof item?.variable === 'string' && item.variable.trim().length > 0;
    });
    editableData.value.selectedToolId = null;
    editableData.value.toolInputMappings = {};
    editableData.value.inlineTool = normalizeInlineTool(editableData.value.inlineTool);
  } else if (currentActionType.value === 'transferCall' || currentActionType.value === 'endCall') {
    editableData.value.config = normalizeCallControlConfig(editableData.value.config, currentActionType.value);
    if (currentActionType.value === 'endCall') {
      editableData.value.config.delayMode = 'afterAssistantReply';
    }
    editableData.value.selectedToolId = null;
    editableData.value.toolInputMappings = {};
    editableData.value.inlineTool = normalizeInlineTool(editableData.value.inlineTool);
  } else if (currentActionType.value === 'announce') {
    editableData.value.config = normalizeAnnounceConfig(editableData.value.config);
    editableData.value.selectedToolId = null;
    editableData.value.toolInputMappings = {};
    editableData.value.inlineTool = normalizeInlineTool(editableData.value.inlineTool);
    editableData.value.useExactMessage = true;
  } else {
    editableData.value.toolInputMappings = editableData.value.toolInputMappings !== undefined && editableData.value.toolInputMappings !== null && typeof editableData.value.toolInputMappings === 'object' ? editableData.value.toolInputMappings : {};
    editableData.value.inlineTool = normalizeInlineTool(editableData.value.inlineTool);
    if (editableData.value.toolSource === 'inlineJsTool') {
      editableData.value.toolSource = 'storedTool';
    }
  }
  emit('save', editableData.value);
  isFullscreenCodeEditorOpen.value = false;
  if (props.renderMode !== 'panel') {
    closeModal();
  }
}
</script>

<style scoped>
.action-step-modal-box {
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.action-step-panel-shell,
.action-step-editor-shell {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.action-step-tabbar {
  display: flex;
  align-items: center;
  padding: 0.15rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.35rem;
  background: #f8fafc;
}

.action-step-tab + .action-step-tab {
  margin-left: 0.5rem;
}

.action-step-tab {
  border: none;
  border-radius: 0.25rem;
  padding: 0.4rem 0.55rem;
  background: transparent;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 600;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.action-step-tab-active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}

.action-step-tab-panel {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 0.45rem;
  padding: 0.55rem 0.6rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.35rem;
  background: #ffffff;
}

.action-step-editor-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.action-step-variable-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.action-step-variable-row > * + * {
  margin-left: 0.5rem;
}

.header-row {
  display: flex;
}

.header-row > * + * {
  margin-left: 0.5rem;
}

.response-mapping-row {
  display: flex;
}

.response-mapping-row > * + * {
  margin-left: 0.5rem;
}

.request-field-row {
  display: flex;
}

.request-field-row > * + * {
  margin-left: 0.5rem;
}

.http-field-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-left: -0.5rem;
  margin-bottom: -0.5rem;
}

.http-field-row > * {
  margin-left: 0.5rem;
  margin-bottom: 0.5rem;
}

.http-field-row .technical-input {
  flex: 1 1 10rem;
  min-width: 0;
}

.technical-input {
  direction: ltr;
  text-align: left;
  unicode-bidi: plaintext;
}

.http-url-input {
  min-height: 2.75rem;
  max-width: 100%;
  resize: vertical;
  overflow-x: hidden;
  overflow-wrap: anywhere;
  word-break: break-all;
  white-space: pre-wrap;
}

.http-editor-actions {
  display: flex;
  flex-wrap: wrap;
}

.http-editor-actions > * + * {
  margin-left: 0.5rem;
}

.transfer-sip-row {
  display: flex;
  align-items: flex-start;
}

.transfer-sip-row > * + * {
  margin-left: 0.5rem;
}

.transfer-sip-phone {
  flex: 1.3;
}

.transfer-sip-ip {
  flex: 1;
}

.transfer-sip-port {
  width: 6rem;
}

.action-step-mapping-box {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  background: #f8fafc;
}

.textarea-small-expandable {
  min-height: 3rem;
  resize: vertical;
}

.action-step-fullscreen-shell {
  position: fixed;
  inset: 0;
  z-index: 1400;
}

.action-step-fullscreen-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(4px);
}

.action-step-fullscreen-panel {
  position: absolute;
  inset: 0.45rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-radius: 0.35rem;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.action-step-fullscreen-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}

.action-step-fullscreen-title-wrap {
  min-width: 0;
}

.action-step-fullscreen-eyebrow {
  color: #64748b;
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.action-step-fullscreen-title {
  margin: 0.2rem 0 0;
  color: #0f172a;
  font-size: 0.85rem;
  font-weight: 600;
}

.action-step-fullscreen-actions {
  margin-left: 1rem;
}

.action-step-fullscreen-body {
  flex: 1;
  min-height: 0;
  padding: 0.5rem;
  background: #f8fafc;
}

.action-step-fullscreen-body :deep(.monaco-editor-container) {
  border-color: #1e293b;
  background-color: #1e1e1e;
}

.action-step-panel-shell :deep(.label-text) {
  font-size: 0.72rem;
  font-weight: 600;
  color: #334155;
}

.action-step-panel-shell :deep(.label) {
  padding-top: 0;
  padding-bottom: 0.2rem;
  min-height: 0;
}

.action-step-panel-shell :deep(.input),
.action-step-panel-shell :deep(.textarea),
.action-step-panel-shell :deep(.select) {
  font-size: 0.78rem;
}

.action-step-panel-shell :deep(.form-control) {
  margin-bottom: 0.55rem;
}
</style>